import { FormEvent, useRef, useState } from 'react'

export type ContactField = 'name' | 'email' | 'phone' | 'message'
export type ContactValues = Record<ContactField, string>
export type ContactErrors = Partial<Record<ContactField, string>>

const initialValues: ContactValues = { name: '', email: '', phone: '', message: '' }

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

function validate(values: ContactValues): ContactErrors {
  const errors: ContactErrors = {}
  if (!values.name.trim()) errors.name = 'Please enter your name.'
  if (!values.email.trim()) errors.email = 'Please enter your email.'
  else if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = 'Please enter a valid email address.'
  if (values.phone.trim() && values.phone.replace(/\D/g, '').length < 7)
    errors.phone = 'Please enter a valid phone number or leave it blank.'
  if (!values.message.trim()) errors.message = 'Please tell us how we can help.'
  return errors
}

/*
 * DESIGN PREVIEW ONLY — client-side validation with NO network requests.
 * Nothing is saved, emailed, or tracked, and no CAPTCHA is simulated.
 *
 * Production integration notes (future implementation, not this preview):
 * - Connect this form to the EXISTING native WebsiteForge form ID 5477207 / frame 7662611.
 * - Preserve that form's existing routing, server-side validation, real CAPTCHA,
 *   and production conversion tracking.
 * - Do NOT recreate, duplicate, or overwrite those production form records,
 *   and do not replace the live form from this design.
 */
export function useContactForm() {
  const [values, setValues] = useState<ContactValues>(initialValues)
  const [errors, setErrors] = useState<ContactErrors>({})
  const [status, setStatus] = useState<'idle' | 'checked'>('idle')
  const formRef = useRef<HTMLFormElement>(null)

  const setField = (field: ContactField, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }))
    if (status !== 'idle') setStatus('idle')
  }

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const nextErrors = validate(values)
    setErrors(nextErrors)
    const firstInvalid = (Object.keys(nextErrors) as ContactField[])[0]
    if (firstInvalid) {
      setStatus('idle')
      formRef.current?.querySelector<HTMLElement>(`[name="${firstInvalid}"]`)?.focus()
      return
    }
    setStatus('checked')
  }

  return { values, errors, status, setField, handleSubmit, formRef }
}
